import type { BacklogMember } from '@ai-meeting/shared'
import { useEffect, useMemo, useState } from 'react'
import PaginationButton from '@/components/PaginationButton'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { ensureBacklogUserId } from '@/services/backlogAccount'
import { pageSlots, visibleRange } from '../projectDirectory'
import {
  memberRoleLabel,
  PROJECT_MEMBERS_PAGE_SIZE,
  sortMembersForDisplay,
} from '../projectMembersUi'

interface ProjectMembersTabProps {
  members: BacklogMember[]
  loaded: boolean
  failed: boolean
}

function memberInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}

function MemberAvatar({ member }: { member: BacklogMember }) {
  const iconUrl = member.icon_url?.trim()
  const initials = memberInitials(member.name)
  const [imageFailed, setImageFailed] = useState(false)
  const showImage = Boolean(iconUrl) && !imageFailed

  if (showImage && iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className="h-10 w-10 shrink-0 rounded-full object-cover"
        loading="lazy"
        decoding="async"
        onError={() => setImageFailed(true)}
      />
    )
  }

  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-semibold text-white"
    >
      {initials}
    </span>
  )
}

/**
 * Who is on the project, as the reader's own Backlog grant sees them.
 *
 * Each row follows the Members tab in Figma: avatar, Backlog display name,
 * mail, and project role, with a "You" / "自分" line on the signed-in reader's
 * own row.
 */
export default function ProjectMembersTab({ members, loaded, failed }: ProjectMembersTabProps) {
  const { t } = useUiStrings()
  const [page, setPage] = useState(1)
  const [currentBacklogUserId, setCurrentBacklogUserId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    void ensureBacklogUserId().then((id) => {
      if (!cancelled) setCurrentBacklogUserId(id)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const sorted = useMemo(
    () => sortMembersForDisplay(members, currentBacklogUserId),
    [members, currentBacklogUserId]
  )

  useEffect(() => {
    setPage(1)
  }, [members])

  const pageCount = Math.max(1, Math.ceil(sorted.length / PROJECT_MEMBERS_PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const sliceStart = (safePage - 1) * PROJECT_MEMBERS_PAGE_SIZE
  const visible = sorted.slice(sliceStart, sliceStart + PROJECT_MEMBERS_PAGE_SIZE)
  const range = visibleRange(safePage, sorted.length, PROJECT_MEMBERS_PAGE_SIZE)

  if (!loaded) return <p className="text-sm text-[var(--text-secondary)]">{t.loading}</p>
  if (failed) {
    return (
      <p role="alert" className="text-sm text-[var(--text-secondary)]">
        {t.projectDetailMembersFailed}
      </p>
    )
  }
  if (members.length === 0) {
    return <p className="text-sm text-[var(--text-secondary)]">{t.projectDetailNoMembers}</p>
  }

  return (
    <div className="flex flex-col gap-6">
      <ul className="flex flex-col gap-3">
        {visible.map((member) => {
          const isSelf = currentBacklogUserId != null && member.id === currentBacklogUserId
          const email = member.mail_address?.trim()
          return (
            <li
              key={member.id}
              className="grid grid-cols-1 gap-1 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-[var(--shadow-sm)] sm:grid-cols-3 sm:items-center sm:gap-4 sm:p-[17px]"
            >
              <div className="flex min-w-0 items-center gap-3">
                <MemberAvatar member={member} />
                <div className="min-w-0">
                  <p className="truncate text-sm text-[var(--text-primary)]">{member.name}</p>
                  {isSelf && (
                    <p className="text-sm text-[var(--text-tertiary)]">{t.projectDetailMemberYou}</p>
                  )}
                </div>
              </div>
              <p className="min-w-0 truncate pl-[52px] text-sm text-[var(--text-primary)] sm:pl-0">
                {email || t.projectDetailMemberEmailMissing}
              </p>
              <p className="pl-[52px] text-sm text-[var(--text-secondary)] sm:pl-0 sm:text-right">
                {memberRoleLabel(member, t)}
              </p>
            </li>
          )
        })}
      </ul>

      {sorted.length > PROJECT_MEMBERS_PAGE_SIZE && (
        <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-[var(--text-secondary)]">
            {fmt(t.projectDetailMembersRange, {
              start: range.start,
              end: range.end,
              total: sorted.length,
            })}
          </p>
          <nav aria-label={t.projectDetailMembers} className="flex flex-wrap items-center gap-1">
            <PaginationButton
              label={t.previousPage}
              disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}
            >
              ‹
            </PaginationButton>
            {pageSlots(safePage, pageCount).map((slot, index) =>
              slot === 'gap' ? (
                <span
                  key={`gap-${index}`}
                  aria-hidden="true"
                  className="px-1 text-xs text-[var(--text-tertiary)]"
                >
                  …
                </span>
              ) : (
                <PaginationButton
                  key={slot}
                  label={fmt(t.pageN, { n: slot })}
                  active={slot === safePage}
                  onClick={() => setPage(slot)}
                >
                  {slot}
                </PaginationButton>
              )
            )}
            <PaginationButton
              label={t.nextPage}
              disabled={safePage >= pageCount}
              onClick={() => setPage(safePage + 1)}
            >
              ›
            </PaginationButton>
          </nav>
        </div>
      )}
    </div>
  )
}
